/**
 * Battle mode — the Total War-shaped half of the game.
 *
 * No economy, no build phase, no Hearth to lose. Two armies, a deployment zone
 * each, and a field. You place your line, you set your reserve, you press
 * "Begin", and then you fight it with the mouse.
 *
 * The three things that make a Total War battle a *battle* rather than an
 * RTS scrap are already in the simulation and are simply turned up here:
 *
 *   Morale wins battles, not health. An army does not need to be killed; it
 *   needs to be broken. Companies rout when their nerve goes, routers drag
 *   their neighbours with them, and when enough of an army is running the whole
 *   thing collapses at once.
 *
 *   Facing and fatigue decide melees. A charge into a flank is worth several
 *   times a charge into a shieldwall, and a company that has run across the
 *   field arrives too tired to cash it in.
 *
 *   Ammunition runs out. A missile line is a timer, not a wall, and protecting
 *   it is the reason you hold cavalry back.
 */

import { MAP_H, MAP_W } from './constants.ts';
import { alliedWith, areHostile, Phase, Team, Terrain, UF, UnitState, type FieldHill, type FieldObstacle } from './types.ts';
import { EV, LogTone, pushEvent, pushLog } from './events.ts';
import { paintObstacleTerrain, rebuildObstacleGrid, scatterObstacles } from './obstacles.ts';
import { bestStand, layBattleGround, pruneBattleResourceNodes, rebuildHeightGrid, type GroundReport } from './field.ts';
import { raiseTown, townRadius } from './systems/aitown.ts';
import { clearNodesInRect } from './systems/nodes.ts';
import { raceOfFaction } from '../data/races.ts';
import {
  createWorld,
  evictFromRect,
  findCompany,
  initRun,
  invalidateCompanyIndex,
  type Company,
  type World,
  type WorldConfig,
} from './world.ts';
import { createCompany, fillCompany, orderCompany } from './systems/companies.ts';
import { foldPermanent, refoldEquipment, rollHoard } from './systems/items.ts';
import { applyHeroPassives, fieldHeroAt } from './systems/hero.ts';
import { Formation, Stance } from './world.ts';
import { unitType, unitTypeIndex, UNIT_TYPES } from '../data/units.ts';
import { faction, FACTIONS, PLAYABLE_FACTIONS } from '../data/factions.ts';
import { makeRng, nextFloat, pick, shuffle, type Rng } from '../core/rng.ts';
import { clamp, clamp01 } from '../core/math.ts';

export type ScenarioKind = 'open_field' | 'hold_the_line' | 'assault' | 'ambush';

export interface ScenarioDef {
  id: ScenarioKind;
  name: string;
  flavour: string;
  /** What the player has to do, in the player's words. */
  objective: string;
  /** Attacker strength as a fraction of the defender's. */
  attackerRatio: number;
  /** The player is the attacker in this scenario. */
  playerAttacks: boolean;
  /** Fortifications on the defender's line. */
  fortified: boolean;
}

export const SCENARIOS: ScenarioDef[] = [
  {
    id: 'open_field',
    name: 'Open Field',
    flavour: 'Two hosts, one field, and a ridge on it that only one of you can have.',
    objective: 'Break them. Even numbers. The ground is the only advantage going.',
    attackerRatio: 1,
    playerAttacks: false,
    fortified: false,
  },
  {
    id: 'hold_the_line',
    name: 'Hold the Line',
    flavour: 'They have more. You have a hedge, a ditch and the morning.',
    objective: 'Survive with a company still standing. They outnumber you three to two.',
    attackerRatio: 1.5,
    playerAttacks: false,
    fortified: true,
  },
  {
    id: 'assault',
    name: 'Assault',
    flavour: 'Somebody decided the wall was in the way.',
    objective: 'Break a dug-in defender who has walls and you do not. Bring siege or bring numbers.',
    attackerRatio: 1.45,
    playerAttacks: true,
    fortified: true,
  },
  {
    id: 'ambush',
    name: 'Ambush',
    flavour: 'They were not expecting you. They are now.',
    objective: 'You start behind them and to the flank. Cash it in before they turn.',
    attackerRatio: 0.85,
    playerAttacks: true,
    fortified: false,
  },
];

export interface BattleArmy {
  factionId: string;
  /** Company ids belonging to this army. */
  companies: number[];
  /** Companies that have been wiped or have routed off the field. */
  broken: number;
  /** 0..1 — the army's collective nerve. Below `BREAK_POINT` it collapses. */
  resolve: number;
  name: string;
  /**
   * Which banner on the field this is. `Team.Player` / `Team.Invader` in a 1v1,
   * plus `Team.Ally` / `Team.Warband` when there are four.
   *
   * Absent in saves written before 2v2 existed, and defaulted there by position
   * — `player` was Team.Player and `enemy` was Team.Invader, always, and the
   * muster calls in `createBattle` are still the only thing that assigns them.
   */
  team: number;
  /**
   * This army has already broken and run.
   *
   * A *side* loses when every army on it has broken, which is what makes a 2v2
   * a 2v2 rather than two duels: your partner collapsing does not end your
   * battle, it ends his. Identical in a 1v1, where a side is one army and the
   * two conditions are the same condition.
   */
  routed: boolean;
}

/**
 * One entry on the player's own order of battle, before the field exists.
 *
 * A count rather than a repeated id, so a stack of four spear companies is one
 * line in the panel and one line in the hash.
 */
export interface LoadoutPick {
  typeIdx: number;
  count: number;
}

/**
 * The army the player chose, as hashed state.
 *
 * This is deliberately not a UI-side construction handed to `createBattle` and
 * then forgotten. It *decides the world* — which companies stand on the field
 * is the single most behaviour-defining fact about a battle — so it is edited
 * through `Cmd.BattleLoadout`, it is hashed inside the battle block, and it is
 * saved. That is what lets the headless harness field a chosen army and a
 * replay reproduce one.
 *
 * `budget` is the purse in threat points and is fixed at `createBattle`;
 * `picks` is what has been spent out of it. `spent` is derived by
 * `loadoutSpent` and deliberately not stored, for the reason the calendar does
 * not store the month.
 */
export interface BattleLoadout {
  budget: number;
  picks: LoadoutPick[];
}

export interface BattleState {
  scenario: ScenarioDef;
  /** The player may re-pick their army. See `BattleConfig.freeMuster`. */
  freeMuster: boolean;
  player: BattleArmy;
  enemy: BattleArmy;
  /**
   * The second banner a side. Null in a 1v1, which is every siege, every
   * campaign battle and every skirmish that did not ask for four.
   *
   * Two nullable fields rather than an array of armies, and the reason is
   * merge cost rather than taste: `b.player` and `b.enemy` are read by the HUD,
   * the save, the hash, the result screen and four tests, and turning them into
   * `b.armies[0]` and `b.armies[1]` would have been a rename in every one of
   * them for no behaviour. `armiesOf` below is the loop everything new uses.
   */
  playerAlly: BattleArmy | null;
  enemyAlly: BattleArmy | null;
  /**
   * The army the player picked, and the purse they picked it out of. See
   * `BattleLoadout`. Present on every battle: a field built the old way — the
   * campaign's, the harness's — simply gets a loadout describing what it was
   * handed, so the panel has something true to show and the hash has one shape
   * to cover rather than two.
   */
  loadout: BattleLoadout;
  /**
   * Trees, thickets and boulders, rolled from this battle's own seed.
   *
   * State, and hashed: where the cover is decides the fight. The tile bitmap
   * derived from it lives on `World.obstacleGrid` — see `src/sim/obstacles.ts`.
   */
  obstacles: FieldObstacle[];
  /**
   * The relief: which side of this field has the high ground.
   *
   * State, and hashed beside the scatter, for the same reason and with the same
   * shape — a short list of discs rather than a map layer. The per-tile height
   * derived from it lives on `World.heightGrid`; see `src/sim/field.ts`, which
   * also explains why every other thing the field generator lays down (the
   * river, the ford, the wood, the road) needs no new state at all.
   *
   * Empty on a field with no relief, which is every field written before this
   * existed and every world that has never been a battle.
   */
  hills: FieldHill[];
  /** True while the player is still placing companies. */
  deploying: boolean;
  /** Seconds elapsed since the battle began. */
  elapsed: number;
  /** 0 = undecided, 1 = player won, -1 = player lost. */
  outcome: number;
  /** Deployment rectangle for the player, in tiles. */
  deployZone: { x0: number; y0: number; x1: number; y1: number };
  /**
   * Both sides are people, and the field belongs to neither.
   *
   * A battle is normally *the* player against a machine: `Team.Player` means
   * "mine", the enemy is steered by the crowd, and the kingdom's own perks
   * ride along. None of that survives two crowns meeting — so a PvP field is
   * built once, canonically, with the attacker as `Team.Player` and the
   * defender as `Team.Enemy`, and every client steps that same world. Which
   * colours are *yours* is a local matter; the field does not care.
   *
   * Two rules change while it is set, both for symmetry: each side follows
   * its own orders rather than one side following the crowd, and neither
   * side brings its bloodline perks, since the world holds one kingdom's
   * mods and handing them to whoever happened to attack is not a fight.
   */
  pvp: boolean;
  /** Points budget each side was built from. */
  points: number;
  log: string[];
}

/** A fresh banner, before anything is mustered onto it. */
export function makeArmy(factionId: string, team: number): BattleArmy {
  return {
    factionId,
    companies: [],
    broken: 0,
    resolve: 1,
    name: faction(factionId).name,
    team,
    routed: false,
  };
}

/** An army breaks when its resolve falls below this. */
const BREAK_POINT = 0.34;
/**
 * No army may break in the opening seconds. Without this, a scenario that
 * starts the two sides in contact can be decided before the player has moved,
 * which is both unfair and extremely stupid to watch.
 */
const GRACE_SECONDS = 12;

export interface BattleConfig extends WorldConfig {
  scenario: ScenarioKind;
  enemyFactionId: string;
  /**
   * Both sides human — see `BattleState.pvp`. The field is then built once,
   * identically, on every machine that is watching it.
   */
  pvp?: boolean;
  /** Army size in threat points per side. */
  points: number;
  /**
   * Exact per-side budgets, overriding the scenario's ratio.
   *
   * The campaign layer sets these: when a world-map battle is handed to this
   * mode, the two armies on the field must be the two armies on the map, not
   * whatever ratio the scenario would otherwise have imposed. A skirmish setup
   * from the menu leaves them unset and gets the scenario's shape.
   */
  playerPoints?: number;
  enemyPoints?: number;
  /**
   * Two crowns a side.
   *
   * Setting either of these is what makes a field a 2v2. They are faction ids,
   * because a second banner is a second *people* — a 2v2 in which both halves
   * of your side are the same roster is a bigger army, not an alliance. Leave
   * them unset and the field is the 1v1 it has always been, byte for byte.
   */
  allyFactionId?: string;
  enemyAllyFactionId?: string;
  /**
   * The army the player brings, instead of one mustered for them.
   *
   * Unset from the campaign and from the harness, which want the muster they
   * have always had. Set from the setup board, where the player has spent the
   * purse themselves.
   */
  loadout?: LoadoutPick[];
  /**
   * The player may re-pick their own order of battle before the horns.
   *
   * **Only a skirmish sets this**, and the reason is what it would mean
   * anywhere else. A campaign battle's army is mustered from the crown's actual
   * points at `battleConfigFor`; letting the player empty it and re-draw from
   * the whole roster at the same budget turns a random levy into an optimal one
   * for free, every battle, for ever. A shared field is worse — one side
   * re-mustering after the other has deployed is not a fight.
   *
   * Off by default, which is the safe direction: a mode that forgets to ask
   * gets the behaviour the game has always had.
   */
  freeMuster?: boolean;
  /**
   * Scatter the field with trees and rocks. Defaults **on** for a battle world
   * and is never true anywhere else — a settlement's trees are its terrain and
   * its economy, and are somebody else's file.
   */
  scatter?: boolean;
  /**
   * Lay relief, water, wood and road on the field. Defaults **on**.
   *
   * The one caller that turns it off is `npm run battleground`, which needs to
   * be able to print the field this mode had *before* the generator existed in
   * order to say by how much it moved — a measurement tool that can only
   * measure the state it is arguing for is not a measurement. Tests use it too,
   * for the far duller reason that a field with no relief is the case where
   * `World.heightGrid` must come back null.
   */
  ground?: boolean;
  /**
   * The defender's town, raised on the field instead of a hedge.
   *
   * Set by `battleConfigFor` off the province being assaulted, and unset
   * everywhere else — a skirmish from the menu and the harness's own fields get
   * the fieldworks they have always had. `tier`, `fort` and `capital` are the
   * strategic layer's own numbers for the place; see `raiseTown`.
   */
  town?: { tier: number; fort: number; capital: boolean };
}

/**
 * Build a battle world. Reuses the whole simulation — the same movement,
 * combat, morale and power systems — and simply sets up a different board.
 */
export function createBattle(config: BattleConfig, cityGround?: World): World {
  const w = createWorld(config);
  initRun(w);

  // Strip the settlement out: a battle has no economy and no villagers.
  for (let i = 0; i < w.units.slots.high; i++) if (w.units.isAlive(i)) w.units.kill(i);
  w.res.timber = 0;
  w.res.stone = 0;
  w.res.grain = 0;
  w.res.souls = 200;
  w.pop = 0;
  w.popCap = 0;
  w.will = 60;
  w.morale = 1;

  const scenario = SCENARIOS.find((s) => s.id === config.scenario) ?? SCENARIOS[0];
  const rng = w.rng;

  if (cityGround) {
    // The caller supplies an isolated copy: combat may break these exact walls
    // and roofs, but the settlement's economy must remain parked until return.
    w.map = cityGround.map;
    w.buildings = cityGround.buildings;
    w.nodes = cityGround.nodes;
    w.hearth = cityGround.hearth;
    w.rubbleCount = cityGround.rubbleCount;
    w.wallTeam = scenario.playerAttacks ? Team.Invader : Team.Player;
    for (let i = 0; i < w.buildings.slots.high; i++) {
      if (w.buildings.slots.alive[i]) w.buildings.team[i] = w.wallTeam;
    }
    w.dirty.flow = true;
  } else {
    flattenField(w, rng);
  }

  const cx = cityGround ? w.map.hearthX : MAP_W / 2;
  const cy = cityGround ? w.map.hearthY : MAP_H / 2;
  const spread = 26;

  /**
   * A siege is fought at a town, and the town decides the geometry.
   *
   * Without one the two lines stand fourteen tiles either side of the middle,
   * exactly as they always have. With one, the defender's walls are between
   * them, so the *wall* is what the distances are measured from: the town sits
   * on the defender's own half, the defender musters just inside the gate that
   * faces the enemy, and the attacker forms up fourteen tiles clear of the
   * stone, whatever radius the holding's rung has earned it. Deriving the line
   * from the radius rather than fixing it means a Hamlet is not besieged from
   * the same distance as a Seat of the Realm.
   *
   * **The town used to be gated on `scenario.playerAttacks`**, and that gate is
   * the thing this pass came here to find. `battleConfigFor` hands a `town`
   * down for *any* battle at a fortified province, and the campaign picks
   * `assault` when a human attacks and `hold_the_line` when a human defends —
   * so exactly half of all sieges of a real holding arrived with the town in
   * hand and threw it away, and the player defending their own walled Market
   * Town got a hedge and a ditch in a field. The door was already there;
   * nothing walked through it. Now the town is raised whenever there is one and
   * it belongs to whoever is dug in.
   */
  const townSpec = config.town ?? null;
  /** Which way the defender faces. The player is south, always. */
  const defSign = scenario.playerAttacks ? -1 : 1;
  const townR = townSpec ? townRadius(townSpec.tier, townSpec.capital) : 0;
  const townY = cityGround ? cy : cy + defSign * 8;
  /**
   * A garrison stands *in* its town, not in front of the gate.
   *
   * This put the defender's muster row five tiles inside the southern wall, and
   * then laid an army forty-six tiles wide across it — on a circuit that is
   * thirty-four tiles across at rung six. Measured, **only 20% of the defenders
   * were inside their own walls**; the rest, both wings and every horseman,
   * formed up in the open in front of the stone they were supposed to be
   * behind. A third of the radius off centre, toward whoever is coming, with
   * the frontage capped to the circuit — see `FRONTAGE_MAX`.
   */
  const defenderY = townSpec ? townY - defSign * townR * 0.34 : cy + defSign * 14;
  /** Wide enough to be a line, narrow enough to fit inside the gate. */
  const defFrontage = townSpec ? Math.max(12, townR * 1.15) : FRONTAGE_MAX;
  // A stored town may have grown far beyond its original tier's circuit.
  // Read the actual outside edge so a new outer wall never encloses the
  // besiegers before the player has even sounded the horn.
  let northEdge = townY - townR;
  let southEdge = townY + townR;
  if (cityGround) {
    for (let k = 0; k < w.map.wall.length; k++) {
      if (!w.map.wall[k]) continue;
      const y = Math.floor(k / MAP_W);
      northEdge = Math.min(northEdge, y);
      southEdge = Math.max(southEdge, y + 1);
    }
  }
  const attackerY = townSpec
    ? (scenario.playerAttacks ? Math.min(MAP_H - 10, southEdge + 14) : Math.max(10, northEdge - 14))
    : cy - defSign * 14;
  let playerY = scenario.playerAttacks ? attackerY : defenderY;
  let enemyY = scenario.playerAttacks ? defenderY : attackerY;

  /**
   * The ground, laid before anybody stands on it.
   *
   * Ridge, water, wood, bog and road — see `src/sim/field.ts`, which also
   * carries the measurement that made this necessary. It comes off `w.rng`
   * ahead of the scatter and the muster, so the field and the armies are
   * consecutive stretches of one stream rather than two orderings of the same
   * one, exactly as the scatter already was.
   */
  const ground: GroundReport =
    config.ground === false || cityGround
      ? { hills: [], woodScatter: [], ridges: 0, copses: 0, water: false, crossings: 0, road: false, bogs: 0, uprooted: 0 }
      : layBattleGround(w, rng, {
          cx,
          cy,
          defenderY,
          biome: config.biome,
          town: townSpec ? { x: cx, y: townY, r: townR } : null,
        });
  rebuildHeightGrid(w, ground.hills);

  /**
   * And then the defender reads it.
   *
   * The side that stands still picks the ground: `bestStand` walks the rows
   * within six tiles of the nominal muster line and takes the one with the most
   * height and the best footing on it. Only when there is no town — a town's
   * geometry is its wall, and a garrison that wandered six tiles off its own
   * gate to stand on a knoll would be outside its own defences.
   */
  if (!townSpec) {
    /**
     * And **both** lines move with it, by the same number of tiles.
     *
     * Moving only the defender was the first cut and it was wrong in a way that
     * took an afternoon to find. The attacking machine's advance is not
     * infinitely patient — it marches its lane, and a defender who has stepped
     * six tiles back onto a crest is six tiles further away than the field was
     * built for. Measured, that turned into two armies standing fourteen tiles
     * apart doing nothing for three hundred seconds, and `npm run battleground
     * -- --fight --wide` counted **14 undecided fields in 40 against the flat
     * board's 8**.
     *
     * A rigid translation of both rows keeps the separation the mode was tuned
     * around byte for byte, and still puts the defender on the hill — which is
     * the whole of what was wanted. The ground decides *where* the battle is
     * fought, not how far apart it starts.
     */
    const shift = bestStand(w, cx, defenderY, spread) - defenderY;
    playerY += shift;
    enemyY += shift;
  }

  /**
   * The player deploys on the south edge of the field, the enemy on the north
   * — or, when the player is the one holding the town, **inside his own wall**.
   *
   * The rectangle is what the player is allowed to shuffle companies inside
   * before the horns, so a zone wider than the circuit is an invitation to
   * deploy the reserve outside the gate and then wonder where the walls went.
   */
  const defendsTown = townSpec !== null && !scenario.playerAttacks;
  const halfW = defendsTown ? Math.max(8, townR * 0.72) : spread;
  const halfH = defendsTown ? Math.max(6, townR * 0.6) : 6;
  const deployZone = { x0: cx - halfW, y0: playerY - halfH, x1: cx + halfW, y1: playerY + halfH };

  const playerPoints =
    config.playerPoints ??
    (scenario.playerAttacks ? config.points * scenario.attackerRatio : config.points);
  const enemyPoints =
    config.enemyPoints ??
    (scenario.playerAttacks ? config.points : config.points * scenario.attackerRatio);

  /**
   * Four banners, two sides.
   *
   * The second banner a side takes half its side's purse, so a 2v2 is the same
   * weight of metal on the field as the 1v1 it replaces — the difference is
   * that half of yours obeys somebody else and half of theirs is a different
   * roster. Making a 2v2 twice as big would have been a different scenario
   * wearing the same name.
   */
  const twoOnTwo = config.allyFactionId !== undefined || config.enemyAllyFactionId !== undefined;
  const share = twoOnTwo ? 0.5 : 1;

  const player = makeArmy(config.factionId, Team.Player);
  const enemy = makeArmy(config.enemyFactionId, Team.Invader);
  const playerAlly = config.allyFactionId ? makeArmy(config.allyFactionId, Team.Ally) : null;
  const enemyAlly = config.enemyAllyFactionId ? makeArmy(config.enemyAllyFactionId, Team.Warband) : null;

  /**
   * The scatter goes down before anybody musters, and the muster rows are kept
   * clear of it.
   *
   * Order matters twice over. `spawnUnit` steps a model out of solid ground it
   * would have been born in, so a rock laid *after* the muster would shove men
   * out of formation, and a rock laid before it simply pushes the two or three
   * models whose slot it swallowed to the nearest open tile. And the roll comes
   * off `w.rng`, ahead of the muster's own draws, so the field and the armies
   * are two consecutive stretches of one stream rather than two orderings of
   * the same one.
   */
  const keepClear = [
    { x0: cx - 40, y0: playerY - 8, x1: cx + 40, y1: playerY + 8 },
    { x0: cx - 40, y0: enemyY - 8, x1: cx + 40, y1: enemyY + 8 },
    // Nothing grows in a town. The whole disc, plus its hinterland.
    ...(townSpec
      ? [{ x0: cx - townR - 10, y0: townY - townR - 10, x1: cx + townR + 10, y1: townY + townR + 10 }]
      : []),
  ];
  /**
   * Two scatters, and the second one is the point.
   *
   * The **open** roll is what has always been here: two or three dozen pieces
   * spread evenly over a 384×384 map. Measured against the box a battle is
   * actually fought in, that is 0.6 pieces of cover per field — a number the
   * mode has been carrying since the scatter was written, and one nobody would
   * have believed without printing it.
   *
   * The **wood** roll comes off the copses `layBattleGround` painted, so the
   * trees are where the trees are. It is filtered by the same muster bands the
   * open roll is kept out of: a wood on a deployment row stays a wood on the
   * ground — slow to cross, and still a wood on the picture — but it does not
   * put a trunk through somebody's front rank before the horn.
   */
  const scatter =
    config.scatter === false || cityGround
      ? []
      : [
          ...scatterObstacles(rng, keepClear),
          ...ground.woodScatter.filter(
            (o) => !keepClear.some((z) => o.x + o.r >= z.x0 && o.x - o.r <= z.x1 && o.y + o.r >= z.y0 && o.y - o.r <= z.y1),
          ),
        ];
  paintObstacleTerrain(w, scatter);
  // Rock props are painted after the broad woodland. Keep tree trunks off
  // their final footprint, as well as the roads and crossings beneath it.
  if (!cityGround && (w.config.biome === 'forest' || w.config.biome === 'jungle')) pruneBattleResourceNodes(w);

  /**
   * Where each banner stands on its own side's frontage.
   *
   * A 1v1 is dead centre, exactly as it always was. A 2v2 puts the two allied
   * armies shoulder to shoulder rather than stacked, so each has a flank of its
   * own to hold and the seam between them is a real place on the field — which
   * is the whole tactical content of a 2v2 and the reason it is not just a
   * bigger army.
   */
  const WING = 27;
  const playerX = twoOnTwo ? cx - WING : cx;
  const allyX = cx + WING;

  const playerBudget = playerPoints * share;
  const playerPicks = config.loadout && config.loadout.length > 0 ? config.loadout : null;
  // Only the side holding the town is squeezed into it. The besieger draws up
  // across the whole field, which is the shape of a siege from the outside.
  const playerFront = scenario.playerAttacks ? FRONTAGE_MAX : defFrontage;
  const enemyFront = scenario.playerAttacks ? defFrontage : FRONTAGE_MAX;
  if (playerPicks) {
    fieldPicks(w, player, playerPicks, playerX, playerY, 0, rng, playerFront);
  } else {
    musterArmy(w, player, Team.Player, playerBudget, playerX, playerY, 0, rng, playerFront);
  }
  musterArmy(w, enemy, Team.Invader, enemyPoints * share, playerX, enemyY, Math.PI, rng, enemyFront);
  if (playerAlly) {
    musterArmy(w, playerAlly, Team.Ally, playerPoints * share, allyX, playerY, 0, rng, playerFront);
  }
  if (enemyAlly) {
    musterArmy(w, enemyAlly, Team.Warband, enemyPoints * share, allyX, enemyY, Math.PI, rng, enemyFront);
  }

  if (scenario.id === 'ambush') {
    // On the enemy's open flank, at charge distance — not on top of them.
    // Dropping the army *inside* the enemy line, which is what this did first,
    // is not an ambush; it is a company being surrounded and broken in ten
    // seconds before the player has touched the mouse.
    const flankX = cx - 34;
    const flankY = cy - 14;
    player.companies.forEach((id, n) => {
      const c = findCompany(w, id);
      if (!c) return;
      const row = n % 3;
      const col = Math.floor(n / 3);
      moveCompanyTo(w, c, flankX - col * 4.5, flankY - 7 + row * 7 + nextFloat(rng) * 1.5, 0);
    });
    deployZone.x0 = flankX - 12;
    deployZone.x1 = flankX + 8;
    deployZone.y0 = flankY - 14;
    deployZone.y1 = flankY + 14;
  }

  /**
   * Whose stonework is on this field.
   *
   * The works belong to whoever is dug in, and on an `assault` that is the
   * enemy. Set *before* the works go down so that `evictFromRect` at the stamp
   * reads the same ownership every later pass will. On every other scenario it
   * stays `Team.Player`, which is what the whole simulation assumed before this
   * field existed. See `World.wallTeam`.
   */
  if (scenario.playerAttacks) w.wallTeam = Team.Invader;

  if (townSpec && !cityGround) {
    /**
     * The town goes down *after* the muster, for the same reason the fieldworks
     * always did: `evictFromRect` at every stamp steps a model out of ground
     * that has just turned solid under it, and a model whose tile turns solid
     * and is *not* stepped out freezes for the whole battle. Raising it first
     * and mustering into it would have needed the muster to know where the
     * streets are, which is a second copy of the layout.
     */
    raiseTown(w, rng, {
      cx,
      cy: townY,
      /**
       * The town belongs to whoever is dug in, which is no longer always the
       * enemy. `w.wallTeam` was set six lines above off exactly the same
       * question, so it is read here rather than answered twice — the two
       * disagreeing is how you get a garrison that will not stand on its own
       * wall because the wall is at war with it.
       */
      team: w.wallTeam,
      tier: townSpec.tier,
      fort: townSpec.fort,
      capital: townSpec.capital,
      // The people, not the banner: `race` on a building type is a race id.
      raceId: raceOfFaction(scenario.playerAttacks ? config.enemyFactionId : config.factionId),
    });
  } else if (scenario.fortified && !cityGround) {
    buildFieldWorks(w, scenario.playerAttacks ? cy - 8 : cy + 8, scenario.playerAttacks);
  }

  w.battle = {
    scenario,
    player,
    enemy,
    playerAlly,
    enemyAlly,
    loadout: { budget: playerBudget, picks: [] },
    // Never on a shared field, whatever the caller asked for. See the note at
    // `BattleConfig.freeMuster`.
    freeMuster: config.freeMuster === true && config.pvp !== true,
    obstacles: scatter,
    hills: ground.hills,
    deploying: true,
    elapsed: 0,
    outcome: 0,
    deployZone,
    pvp: config.pvp === true,
    points: config.points,
    log: [],
  };
  // Whatever is standing there *is* the loadout, however it got there — a
  // campaign muster, the harness, or the player's own picks. One shape for the
  // panel to read and one for the hash to cover.
  w.battle.loadout.picks = picksOfArmy(w, player);
  rebuildObstacleGrid(w);

  w.phase = Phase.Build;
  w.phaseT = Number.POSITIVE_INFINITY;
  pushLog(w.events, `${scenario.name}. ${scenario.objective}`, LogTone.Lore);
  pushLog(w.events, groundLine(ground), LogTone.Lore);
  return w;
}

/**
 * The ground, in the sentence a scout would say it in.
 *
 * Read straight off the generator's own report rather than off the map, so the
 * line the player is told and the ground the simulation reads cannot come
 * apart. It is a log line and nothing else — no code branches on it.
 */
function groundLine(g: GroundReport): string {
  const parts: string[] = [];
  if (g.hills.length > 0) {
    let crest = 0;
    for (const h of g.hills) crest = Math.max(crest, h.h);
    parts.push(crest > 2.6 ? 'a ridge over the field' : 'broken ground');
  }
  if (g.water) {
    parts.push(g.crossings > 1 ? 'a river with a ford and a bridge' : 'a river, and one way over it');
  }
  if (g.copses > 0) parts.push(g.copses > 1 ? 'woods on the flanks' : 'a wood');
  if (g.bogs > 0) parts.push('wet ground');
  if (g.road) parts.push('a road through');
  if (parts.length === 0) return 'Open ground, and nothing on it.';
  return `The ground: ${parts.join(', ')}.`;
}

/**
 * Send the character — and his cart — onto the field.
 *
 * A field battle builds a *fresh* world and musters an army into it from
 * points, which meant the man the whole campaign is about was sitting in a
 * parked capital while his army fought without him. The hero already fields as
 * a company of one during a Siege; this is the same thing for the mode that is
 * nothing but fighting, and it is what makes a won field a place his kit can
 * come from.
 *
 * What crosses over, and what does not, is the design:
 *
 *   • **He does**: class, name, level, experience, bought skills, and every
 *     item on the cart, worn exactly as it was worn at home.
 *   • **The realm does not**: relics, tech nodes and Chronicle answers stay
 *     with the settlement that bought them. The base bag is rebuilt from
 *     `defaultMods()` and given only his own passives, which are re-derivable
 *     from the catalogue and therefore cannot drift. A campaign that let the
 *     capital's forty tech nodes onto every skirmish field would make the
 *     battle mode a readout of the city builder.
 *
 * He arrives on his own side of the deploy zone, behind the line, which is the
 * one place a company of one is not immediately dead.
 */
export function carryCharacterInto(dst: World, src: World): boolean {
  if (src.hero.classId === '') return false;
  if (dst.hero.classId !== '') return false;

  dst.hero = {
    ...src.hero,
    skills: src.hero.skills.slice(),
    cooldowns: { ...src.hero.cooldowns },
    // Handles name entities in the world they came from. He is spawned fresh
    // here, and he arrives on his feet however he left.
    unitH: -1,
    companyId: -1,
    fallen: false,
    reviveT: 0,
  };
  dst.inventory = src.inventory.map((s) => ({ ...s }));
  dst.equipped = src.equipped.slice();
  dst.nextItemUid = src.nextItemUid;
  dst.rngSpoils = makeRng(src.rngSpoils.s);

  foldPermanent(dst, () => applyHeroPassives(dst));

  const z = dst.battle?.deployZone;
  const x = z ? (z.x0 + z.x1) / 2 : MAP_W / 2;
  const y = z ? z.y1 - 1.5 : MAP_H / 2 + 16;
  if (!fieldHeroAt(dst, x, y)) return false;
  if (dst.battle && !dst.battle.player.companies.includes(dst.hero.companyId)) {
    dst.battle.player.companies.push(dst.hero.companyId);
  }
  pushLog(dst.events, `${dst.hero.name} takes the field.`, LogTone.Lore);
  return true;
}

/**
 * Bring him home again: what he learned, and what he picked up off the ground.
 *
 * Deliberately *not* his body, his cooldowns or whether he fell — those belong
 * to the field he fought on. Skills cannot change during a battle (see
 * `heroSkillError`), so his passives are already folded into the world he is
 * returning to and must not be folded a second time; only the numbers that
 * cannot double-count come back.
 */
export function takeCharacterHome(src: World, dst: World): boolean {
  if (src.hero.classId === '' || dst.hero.classId !== src.hero.classId) return false;
  dst.hero.xp = src.hero.xp;
  dst.hero.level = src.hero.level;
  dst.inventory = src.inventory.map((s) => ({ ...s }));
  dst.equipped = src.equipped.slice();
  dst.nextItemUid = src.nextItemUid;
  dst.rngSpoils = makeRng(src.rngSpoils.s);
  refoldEquipment(dst);
  return true;
}

/** A battlefield wants to be a field: open, walkable, with a little cover. */
function flattenField(w: World, rng: Rng): void {
  const map = w.map;
  for (let i = 0; i < map.terrain.length; i++) {
    // Rivers, marsh and the settlement's road star all go: this is a field
    // somebody chose to fight on, not a town somebody built.
    /**
     * `Deep` and `Bridge` are on this list and were not, and finding that out
     * cost a measurement.
     *
     * `Terrain.Deep` was **appended** to the enum after this function was
     * written, and every water test in the game that predates it reads
     * `=== Terrain.River` — the exact bug `isWater` in `map.ts` has a
     * paragraph about, in the one file nobody had checked. So the settlement
     * generator's big river survived the flattening, and `npm run battleground
     * --before --terrain` found **5.6% of the average fight box was deep
     * water**: an unannounced, uncrossable channel that neither the scenario,
     * the muster nor the deployment zone had ever heard of. It did nothing
     * only because terrain never slowed anybody down; the moment `groundSpeed`
     * existed it would have become an invisible wall through the middle of
     * some fields and not others.
     *
     * A field starts genuinely clean now, and the water on it is water
     * `layBattleGround` put there on purpose, with a way over it.
     */
    if (
      map.terrain[i] === Terrain.River ||
      map.terrain[i] === Terrain.Deep ||
      map.terrain[i] === Terrain.Bridge ||
      map.terrain[i] === Terrain.Marsh ||
      map.terrain[i] === Terrain.Road ||
      map.terrain[i] === Terrain.Ruin
    ) {
      map.terrain[i] = Terrain.AshPlain;
    }
    map.wall[i] = 0;
    map.wallHp[i] = 0;
    map.building[i] = -1;
    map.rubble[i] = 0;
  }
  // Remove every building including the Hearth: nothing to defend but the army.
  for (let i = 0; i < w.buildings.slots.high; i++) {
    if (w.buildings.isAlive(i)) w.buildings.kill(i);
  }
  w.hearth = -1;
  w.rubbleCount = 0;

  /**
   * The copses used to be painted here, fourteen of them, as terrain and
   * nothing else: a wood you could see, walk through at full speed, shoot
   * through and stand in without gaining anything. They are gone, and
   * `scatterObstacles` in `createBattle` lays down real ones in their place —
   * things that stop a man, stop an arrow and hide the man behind them. See
   * `src/sim/obstacles.ts`.
   */
  map.terrainVersion++;
  w.dirty.flow = true;
  void rng;
}

/** A hedge-and-ditch line for the defender in fortified scenarios. */
function buildFieldWorks(w: World, rowY: number, playerIsAttacker: boolean): void {
  const map = w.map;
  const y = Math.round(rowY);
  const x0 = Math.round(MAP_W / 2 - 22);
  const x1 = Math.round(MAP_W / 2 + 22);
  for (let x = x0; x <= x1; x++) {
    // Leave two gaps, so the line can be flanked and the defender has to think.
    if (Math.abs(x - (MAP_W / 2 - 9)) < 2) continue;
    if (Math.abs(x - (MAP_W / 2 + 9)) < 2) continue;
    if (!map.inBounds(x, y)) continue;
    const k = map.idx(x, y);
    // Fieldworks clear the whole intersecting outcrop just as a settlement
    // wall does; a breached wall must not leave an indestructible mineral plug.
    clearNodesInRect(w.nodes, x, y, x, y);
    map.wall[k] = 1;
    map.wallMaxHp[k] = 380;
    map.wallHp[k] = 380;
    map.wallTier[k] = 1;
    // The works are stamped *after* the armies muster, so a deep loose
    // company can have a model standing exactly on this row — and a model
    // whose tile turns solid under it freezes for the whole battle (the
    // foundations stall, in a hedge). Same door as every other stamp:
    // evict at the stone. Deliberately not routed through `setWall`, which
    // would also apply `mods.wallHp` a fieldwork does not pay.
    evictFromRect(w, x, y, x, y);
  }
  void playerIsAttacker;
  w.dirty.flow = true;
}

/**
 * What one company of this type costs, in threat points.
 *
 * The whole of the budget arithmetic, in one place, because there are now three
 * callers who must agree on it exactly: the auto-muster below, the player's own
 * loadout, and the panel that has to grey a button out. `t.cost` in
 * `data/units.ts` is per model and is *coin*; this is the points a battle is
 * measured in and is per company. Getting the two confused is the bug that once
 * handed out three times the army it charged for.
 */
/**
 * How wide an army draws itself up, at most.
 *
 * A constant since the mode was written, and it is now a *default* rather than
 * a constant because a garrison has to fit inside its own wall. A Hamlet's
 * circuit is twenty tiles across; a line forty-six tiles wide mustered in the
 * middle of it puts both wings and every horseman outside the gate, standing in
 * front of the stone they are supposed to be behind — which is what a defended
 * holding looked like when the town was raised for it.
 */
export const FRONTAGE_MAX = 46;

export function companyPoints(typeIdx: number): number {
  const t = unitType(typeIdx);
  return t.threat * t.companySize;
}

/** Everything in `pool` that a purse of `ceiling` will bear. */
export function affordableTypes(pool: readonly number[], ceiling: number): number[] {
  return pool.filter((i) => companyPoints(i) <= ceiling);
}

/**
 * How far over its share an auto-mustered arm may go.
 *
 * A company is bought whole or not at all, so a role whose share is 300 and
 * whose cheapest company is 340 should field it rather than stand empty;
 * overshooting by two fifths is the price of not having fractional companies,
 * and it is bounded, which the rule this replaced was not.
 *
 * Deliberately **not** applied to the player's own purse: an arm of an AI army
 * rounding up is a shape decision, and a player being quietly allowed forty per
 * cent more than the board says they have is a lie.
 */
const MUSTER_SLACK = 1.4;

/** The roster this faction musters out of, as type indices. */
export function battleRoster(factionId: string): number[] {
  return faction(factionId).roster.map((id) => unitTypeIndex(id));
}

/**
 * Build an army from a faction's roster, spending a points budget on a sane
 * mix. Battle armies are not random piles: every army gets a line to hold the
 * middle, shot to soften, a flanking arm, and something heavy.
 */
function musterArmy(
  w: World,
  army: BattleArmy,
  team: number,
  points: number,
  cx: number,
  cy: number,
  facing: number,
  rng: Rng,
  maxFrontage = FRONTAGE_MAX,
): void {
  void team;
  const roster = battleRoster(army.factionId);
  const line = roster.filter((i) => isLine(i));
  const shot = roster.filter((i) => (unitType(i).flags & UF.Ranged) !== 0);
  const horse = roster.filter((i) => (unitType(i).flags & UF.Cavalry) !== 0);
  const heavy = roster.filter((i) => (unitType(i).flags & (UF.Large | UF.Siege | UF.Champion)) !== 0);

  const plan: Array<{ pool: number[]; share: number }> = [
    { pool: line.length ? line : roster, share: 0.42 },
    { pool: shot.length ? shot : roster, share: 0.24 },
    { pool: horse.length ? horse : line.length ? line : roster, share: 0.2 },
    { pool: heavy.length ? heavy : line.length ? line : roster, share: 0.14 },
  ];

  /**
   * Draw from what the role's purse can bear, rather than drawing freely and
   * breaking when the draw is too dear.
   *
   * The old shape broke out of the role on the first over-budget pick, which
   * meant one unlucky draw of the dearest company on the roster silently deleted
   * an entire arm of the army — and it took whatever it drew first regardless of
   * price. That was survivable while every roster was flat. It is not survivable
   * now: a company of twelve Hollowed is twelve hundred points, and a field
   * whose line arm drew one had spent its whole army on a single block of foot.
   */
  const built: LoadoutPick[] = [];
  for (const { pool, share } of plan) {
    let budget = points * share;
    let guard = 0;
    while (budget > 0 && guard++ < 12) {
      const afford = affordableTypes(pool, budget * MUSTER_SLACK);
      if (afford.length === 0) break;
      const idx = pick(rng, afford);
      built.push({ typeIdx: idx, count: unitType(idx).companySize });
      budget -= companyPoints(idx);
    }
  }
  if (built.length === 0) {
    // Nothing on the roster fits any arm's share. Take the cheapest company
    // there is: a field with one block of spears on it is still a battle.
    let best = roster[0];
    for (const i of roster) if (companyPoints(i) < companyPoints(best)) best = i;
    built.push({ typeIdx: best, count: unitType(best).companySize });
  }

  shuffle(rng, built);
  layOutArmy(w, army, built, cx, cy, facing, maxFrontage);
}

/**
 * Put a chosen order of battle on the ground.
 *
 * The player's half of the muster: same layout, same formations, same
 * arithmetic — the only difference is that a person picked the list instead of
 * the dice. Shuffled off the same stream so the left-to-right order of a line
 * is not simply the order the player clicked, which would make the first pick
 * always the left flank.
 */
function fieldPicks(
  w: World,
  army: BattleArmy,
  picks: readonly LoadoutPick[],
  cx: number,
  cy: number,
  facing: number,
  rng: Rng,
  maxFrontage = FRONTAGE_MAX,
): void {
  const built: LoadoutPick[] = [];
  for (const p of picks) {
    const n = Math.max(0, Math.min(24, Math.round(p.count)));
    for (let k = 0; k < n; k++) {
      built.push({ typeIdx: p.typeIdx, count: unitType(p.typeIdx).companySize });
    }
  }
  if (built.length === 0) return;
  shuffle(rng, built);
  layOutArmy(w, army, built, cx, cy, facing, maxFrontage);
}

/**
 * Lay an army out: shot in front to skirmish, line behind it, horse on the
 * wings, heavy in the centre of the second rank.
 *
 * Lifted whole out of `musterArmy` so the player's chosen army and the machine's
 * drawn one stand in the same shape. Two formations of the same list must not
 * depend on where the list came from.
 */
function layOutArmy(
  w: World,
  army: BattleArmy,
  built: LoadoutPick[],
  cx: number,
  cy: number,
  facing: number,
  maxFrontage = FRONTAGE_MAX,
): void {
  const frontage = Math.min(maxFrontage, 5 + built.length * 5);
  const lines = built.filter((b) => isLine(b.typeIdx));
  const shots = built.filter((b) => (unitType(b.typeIdx).flags & UF.Ranged) !== 0);
  const horses = built.filter((b) => (unitType(b.typeIdx).flags & UF.Cavalry) !== 0);
  const rest = built.filter((b) => !lines.includes(b) && !shots.includes(b) && !horses.includes(b));

  const fwd = facing === 0 ? -1 : 1;
  placeRow(w, army, army.team, shots, cx, cy + fwd * 4, frontage, facing, Formation.Loose);
  placeRow(w, army, army.team, lines, cx, cy, frontage, facing, Formation.Line);
  placeRow(w, army, army.team, rest, cx, cy - fwd * 5, frontage * 0.5, facing, Formation.Line);
  // Wings.
  horses.forEach((h, n) => {
    const side = n % 2 === 0 ? -1 : 1;
    makeCompany(w, army, army.team, h, cx + side * (frontage * 0.62), cy, facing, Formation.Wedge);
  });
}

function isLine(i: number): boolean {
  const t = unitType(i);
  return (t.flags & (UF.Ranged | UF.Cavalry | UF.Large | UF.Siege | UF.Champion | UF.Caster)) === 0;
}

function placeRow(
  w: World,
  army: BattleArmy,
  team: number,
  group: Array<{ typeIdx: number; count: number }>,
  cx: number,
  cy: number,
  frontage: number,
  facing: number,
  formation: number,
): void {
  if (group.length === 0) return;
  const step = frontage / group.length;
  const start = cx - frontage / 2 + step / 2;
  group.forEach((g, n) => {
    makeCompany(w, army, team, g, start + n * step, cy, facing, formation);
  });
}

function makeCompany(
  w: World,
  army: BattleArmy,
  team: number,
  spec: { typeIdx: number; count: number },
  x: number,
  y: number,
  facing: number,
  formation: number,
): Company {
  const px = clamp(x, 4, MAP_W - 4);
  const py = clamp(y, 4, MAP_H - 4);
  const c = createCompany(w, spec.typeIdx, team, px, py);
  c.facing = facing;
  c.formation = formation;
  c.stance = Stance.Hold;
  c.orderX = px;
  c.orderY = py;
  fillCompany(w, c, spec.count, px, py);
  army.companies.push(c.id);
  return c;
}

function moveCompanyTo(w: World, c: Company, x: number, y: number, facing: number): void {
  const dx = x - c.ax;
  const dy = y - c.ay;
  c.ax = x;
  c.ay = y;
  c.orderX = x;
  c.orderY = y;
  c.facing = facing;
  for (const h of c.members) {
    const i = w.units.resolve(h);
    if (i < 0) continue;
    w.units.x[i] = clamp(w.units.x[i] + dx, 2, MAP_W - 2);
    w.units.y[i] = clamp(w.units.y[i] + dy, 2, MAP_H - 2);
    w.units.goalX[i] = w.units.x[i];
    w.units.goalY[i] = w.units.y[i];
  }
}

// ── Per-tick battle logic ──────────────────────────────────────────────────

/**
 * Resolve, and the collapse that follows losing it.
 *
 * This is the heart of the mode. Battles are decided by nerve: an army whose
 * companies are dying, running or being taken in the flank loses its resolve,
 * and once resolve breaks, everything left on the field routs at once. That
 * moment — the whole line going at the same instant — is the thing worth
 * building the mode around.
 */
export function stepBattle(w: World, dt: number): void {
  const b = w.battle;
  if (!b || b.outcome !== 0) return;
  if (b.deploying) return;

  b.elapsed += dt;

  const all = armies(b);
  for (const a of all) a.resolve = armyResolve(w, a, a.team);

  if (b.elapsed < GRACE_SECONDS) return;

  /**
   * An army breaks on its own nerve; a **side** loses when every army on it has.
   *
   * In a 1v1 those are the same sentence, and this runs the same branch on the
   * same tick as the two-armed version it replaces. In a 2v2 they come apart,
   * and that difference is the mode: your partner's line going is a disaster
   * you have to fight through rather than a defeat you share, and finishing a
   * broken enemy off while his ally is still standing is a decision.
   */
  for (const a of all) {
    if (a.routed || a.resolve >= BREAK_POINT) continue;
    a.routed = true;
    breakArmy(w, a, a.team);
    pushLog(
      w.events,
      alliedWith(a.team, Team.Player) ? `${a.name} break.` : `${a.name} break. Their line is going.`,
      alliedWith(a.team, Team.Player) ? LogTone.Dire : LogTone.Good,
    );
  }

  const foeStanding = all.some((a) => areHostile(a.team, Team.Player) && !a.routed);
  const homeStanding = all.some((a) => alliedWith(a.team, Team.Player) && !a.routed);

  if (!foeStanding) {
    b.outcome = 1;
    pushLog(w.events, 'The field is yours.', LogTone.Good);
    // A won field always gives something up, and gives up better things than a
    // held wall does. This is the mode the character is actually standing in.
    rollHoard(w, 1);
    pushEvent(w.events, EV.WaveEnd, MAP_W / 2, MAP_H / 2, 1, 0, Team.Player);
  } else if (!homeStanding) {
    b.outcome = -1;
    w.gameOver = true;
    w.causeOfDeath = 'The line broke, and then everything behind it.';
    pushLog(w.events, 'Your army breaks.', LogTone.Dire);
  }
}

/** Every banner on the field, in a fixed order. Two in a 1v1, four in a 2v2. */
export function armies(b: BattleState): BattleArmy[] {
  const out = [b.player, b.enemy];
  if (b.playerAlly) out.push(b.playerAlly);
  if (b.enemyAlly) out.push(b.enemyAlly);
  return out;
}

/** Every banner on `team`'s own side. */
export function alliedArmies(b: BattleState, team: number): BattleArmy[] {
  return armies(b).filter((a) => alliedWith(a.team, team));
}

/** Every banner `team` is at war with. */
export function hostileArmies(b: BattleState, team: number): BattleArmy[] {
  return armies(b).filter((a) => areHostile(a.team, team));
}

/** Company ids of everything `team` is at war with. Read by the battle-mode AI. */
export function hostileCompanyIds(b: BattleState, team: number): number[] {
  const out: number[] = [];
  for (const a of armies(b)) if (areHostile(a.team, team)) for (const id of a.companies) out.push(id);
  return out;
}

/**
 * An army's nerve. Weighted by company strength, so losing a big veteran
 * company hurts more than losing a skirmish screen, and heavily penalised for
 * companies that are currently running.
 */
export function armyResolve(w: World, army: BattleArmy, team: number): number {
  let total = 0;
  let holding = 0;
  let broken = 0;

  for (const id of army.companies) {
    const c = findCompany(w, id);
    if (!c) continue;
    const weight = Math.max(1, c.maxSize);
    total += weight;
    if (c.dead || c.members.length === 0) {
      broken++;
      continue;
    }
    let routing = 0;
    let morale = 0;
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i < 0) continue;
      if (w.units.state[i] === UnitState.Routing) routing++;
      morale += w.units.morale[i];
    }
    const alive = c.members.length;
    const routFrac = alive > 0 ? routing / alive : 1;
    const strength = alive / Math.max(1, c.maxSize);
    if (routFrac > 0.5 || strength < 0.2) broken++;
    // A company contributes its strength, its nerve, and its not-running-ness.
    holding += weight * strength * (1 - routFrac) * clamp01(morale / Math.max(1, alive));
  }
  army.broken = broken;
  void team;
  return total > 0 ? clamp01(holding / total) : 0;
}

/** The rout. Everything left on the field turns and runs. */
function breakArmy(w: World, army: BattleArmy, team: number): void {
  for (const id of army.companies) {
    const c = findCompany(w, id);
    if (!c || c.dead) continue;
    c.stance = Stance.Rally;
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i < 0) continue;
      if ((w.units.flags[i] & (UF.Undead | UF.Summoned)) !== 0) continue;
      w.units.state[i] = UnitState.Routing;
      w.units.stateT[i] = 60;
      w.units.morale[i] = 0;
      w.units.fearT[i] = 30;
    }
  }
  void team;
}

/** End deployment and start the fighting. */
export function beginBattle(w: World): void {
  const b = w.battle;
  if (!b || !b.deploying) return;
  b.deploying = false;
  w.phase = Phase.Siege;
  w.phaseT = 0;

  /**
   * Everything the player does not personally command gets its orders here.
   *
   * That is the enemy in a 1v1 — exactly as before, same stance and same lane —
   * and in a 2v2 it is the enemy's ally *and the player's own*, who is a crown
   * with its own army and not a set of companies to drag around. Each side's
   * attackers advance toward the middle from their own half, so an ally on the
   * player's side pushes north and the two hordes push south.
   */
  const enemyAttacks = !b.scenario.playerAttacks;
  for (const a of armies(b)) {
    if (a.team === Team.Player) continue;
    const home = alliedWith(a.team, Team.Player);
    const attacks = home ? b.scenario.playerAttacks : enemyAttacks;
    /**
     * How far to advance, and it is **the enemy's line, not the middle of the
     * map**.
     *
     * This read `MAP_H / 2 ± 4` — a constant, and one that was quietly load
     * bearing. Once a defender's muster row moves (see `bestStand`, which puts
     * him on the ridge and can carry him six tiles back from the nominal line)
     * an attacker ordered to the map's centre halts *out of bow range of a line
     * that is no longer there*, and then never closes: an ordered advance ends
     * at its order, and the free-steering branch that should take over from
     * there is measured in `stepCompanies` against a foe it cannot reach in one
     * lane. Two armies fourteen tiles apart for three hundred seconds, and
     * `npm run battleground -- --fight` counted it as 14 undecided fields in 40
     * where the flat board had 8.
     *
     * Read off the standing enemy rather than off the geometry, because the
     * geometry now has three sources — the scenario, the town's radius and the
     * ground — and a fourth copy of it here would be the one that falls behind.
     * Two tiles short of his line: close enough that contact is inevitable,
     * far enough that the order does not try to walk through him.
     */
    let foeY = MAP_H / 2 + (home ? -4 : 4);
    let foeN = 0;
    let foeSum = 0;
    for (const f of armies(b)) {
      if (!areHostile(a.team, f.team)) continue;
      for (const id of f.companies) {
        const fc = findCompany(w, id);
        if (!fc || fc.dead) continue;
        foeSum += fc.ay;
        foeN++;
      }
    }
    if (foeN > 0) foeY = foeSum / foeN;
    for (const id of a.companies) {
      const c = findCompany(w, id);
      if (!c) continue;
      c.stance = attacks ? Stance.Pursue : Stance.Guard;
      if (attacks) {
        // Advance straight ahead, each company holding its place in the line.
        // Funnelling them all at one point turns an army into a mob, which looks
        // wrong and plays worse.
        orderCompany(w, c, c.ax, foeY - Math.sign(foeY - c.ay) * 2, true);
      }
    }
  }
  pushLog(w.events, 'The horns. Nothing to be done about it now.', LogTone.Dire);
  pushEvent(w.events, EV.WaveStart, MAP_W / 2, MAP_H / 2, 1, 0, Team.Invader);
}

// ── The loadout ────────────────────────────────────────────────────────────

/**
 * What the picks have cost so far.
 *
 * Derived and deliberately not stored, for the reason the calendar does not
 * store the month: a total that can disagree with the list it is a total of is
 * a bug waiting for the first edit.
 */
export function loadoutSpent(l: BattleLoadout): number {
  let n = 0;
  for (const p of l.picks) n += companyPoints(p.typeIdx) * p.count;
  return n;
}

/** What is left in the purse. Never negative. */
export function loadoutRemaining(l: BattleLoadout): number {
  return Math.max(0, l.budget - loadoutSpent(l));
}

/**
 * Why this company cannot be added, or '' if it can.
 *
 * Exported for the same reason `musterRoom` is: the board has to be able to
 * grey a button *and say why*. Strict against the purse — no `MUSTER_SLACK`
 * here, see the note on that constant.
 */
export function loadoutError(w: World, l: BattleLoadout, typeIdx: number): string {
  if (!w.battle) return 'There is no battle.';
  if (!w.battle.deploying) return 'The horns have gone. This is the army you have.';
  if (!battleRoster(w.battle.player.factionId).includes(typeIdx)) {
    return 'They do not answer to you.';
  }
  const cost = companyPoints(typeIdx);
  if (cost > loadoutRemaining(l)) {
    return `${Math.ceil(cost)} points, and you have ${Math.floor(loadoutRemaining(l))}.`;
  }
  return '';
}

/** The order of battle a standing army implies. Used to seed the panel. */
function picksOfArmy(w: World, a: BattleArmy): LoadoutPick[] {
  const counts = new Map<number, number>();
  for (const id of a.companies) {
    const c = findCompany(w, id);
    if (!c) continue;
    counts.set(c.typeIdx, (counts.get(c.typeIdx) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((x, y) => x[0] - y[0])
    .map(([typeIdx, count]) => ({ typeIdx, count }));
}

export const LoadoutAct = {
  Add: 0,
  Remove: 1,
  Clear: 2,
  /** Spend whatever is left the way the machine would. */
  Auto: 3,
} as const;

/**
 * Edit the player's order of battle, and rebuild the army from it.
 *
 * The whole of `Cmd.BattleLoadout`. Deployment only — once the horns have gone
 * the army on the field is the army you have — and it re-fields the *whole*
 * player army on every edit rather than adding a company to it. That is
 * deliberate: a company added to a standing line has no slot in it, and
 * incremental placement would be a second layout rule to keep in step with
 * `layOutArmy`. A deployment-phase rebuild of a few dozen companies is cheap and
 * it cannot drift.
 *
 * Returns false when nothing changed, so the caller can say so.
 */
export function applyLoadout(w: World, act: number, typeIdx: number): boolean {
  const b = w.battle;
  if (!b || !b.deploying || !b.freeMuster) return false;
  const l = b.loadout;

  switch (act) {
    case LoadoutAct.Add: {
      if (loadoutError(w, l, typeIdx) !== '') return false;
      const at = l.picks.find((p) => p.typeIdx === typeIdx);
      if (at) at.count++;
      else l.picks.push({ typeIdx, count: 1 });
      break;
    }
    case LoadoutAct.Remove: {
      const n = l.picks.findIndex((p) => p.typeIdx === typeIdx);
      if (n < 0) return false;
      l.picks[n].count--;
      if (l.picks[n].count <= 0) l.picks.splice(n, 1);
      break;
    }
    case LoadoutAct.Clear:
      if (l.picks.length === 0) return false;
      l.picks.length = 0;
      break;
    case LoadoutAct.Auto: {
      /**
       * Fill the rest of the purse from the same roster, cheapest arm first.
       *
       * Off `w.rng`, which is the battle's own hashed stream, so the button is
       * as replayable as every other command. It uses the same
       * `affordableTypes` the machine's muster does — there is one piece of
       * budget arithmetic in this file and this is it.
       */
      const roster = battleRoster(b.player.factionId);
      let guard = 0;
      let added = false;
      for (;;) {
        const afford = affordableTypes(roster, loadoutRemaining(l));
        if (afford.length === 0 || guard++ > 64) break;
        const idx = pick(w.rng, afford);
        const at = l.picks.find((p) => p.typeIdx === idx);
        if (at) at.count++;
        else l.picks.push({ typeIdx: idx, count: 1 });
        added = true;
      }
      if (!added) return false;
      break;
    }
    default:
      return false;
  }

  refieldPlayerArmy(w);
  return true;
}

/** Tear the player's army down and stand the current picks up in its place. */
function refieldPlayerArmy(w: World): void {
  const b = w.battle;
  if (!b) return;
  const heroes = new Set([w.hero.companyId, ...w.companions.map((h) => h.companyId)]);
  const removed = new Set(b.player.companies.filter((id) => !heroes.has(id)));
  for (const id of b.player.companies) {
    if (!removed.has(id)) continue;
    const c = findCompany(w, id);
    if (!c) continue;
    for (const h of c.members) {
      const i = w.units.resolve(h);
      if (i >= 0) w.units.kill(i);
    }
    c.members.length = 0;
    c.dead = true;
  }
  w.companies = w.companies.filter((c) => !removed.has(c.id));
  invalidateCompanyIndex(w);
  b.player.companies = b.player.companies.filter((id) => heroes.has(id));
  b.player.broken = 0;
  b.player.resolve = 1;
  b.player.routed = false;

  const z = b.deployZone;
  fieldPicks(
    w,
    b.player,
    b.loadout.picks,
    (z.x0 + z.x1) / 2,
    (z.y0 + z.y1) / 2,
    b.scenario.id === 'ambush' ? 0 : 0,
    w.rng,
  );
}

/** The loadout as the setup board wants to read it. Nothing here writes. */
export function loadoutReport(w: World): {
  budget: number;
  spent: number;
  remaining: number;
  picks: Array<{ typeIdx: number; count: number; name: string; points: number }>;
  offer: Array<{ typeIdx: number; name: string; points: number; affordable: boolean }>;
  /** Whether the list may be edited at all. See `BattleConfig.freeMuster`. */
  freeMuster: boolean;
} {
  const b = w.battle;
  const l = b?.loadout ?? { budget: 0, picks: [] };
  const left = loadoutRemaining(l);
  return {
    budget: l.budget,
    spent: loadoutSpent(l),
    remaining: left,
    freeMuster: b?.freeMuster === true,
    picks: l.picks.map((p) => ({
      typeIdx: p.typeIdx,
      count: p.count,
      name: unitType(p.typeIdx).name,
      points: companyPoints(p.typeIdx),
    })),
    offer: b
      ? battleRoster(b.player.factionId).map((i) => ({
          typeIdx: i,
          name: unitType(i).name,
          points: companyPoints(i),
          affordable: companyPoints(i) <= left,
        }))
      : [],
  };
}

/** Available enemy factions for the battle setup screen. */
export function battleFactions(): string[] {
  return FACTIONS.map((f) => f.id);
}

export function playableFactionIds(): string[] {
  return PLAYABLE_FACTIONS.map((f) => f.id);
}

/** A one-line army summary for the setup and result screens. */
export function armySummary(w: World, army: BattleArmy): string {
  const counts = new Map<number, number>();
  for (const id of army.companies) {
    const c = findCompany(w, id);
    if (!c) continue;
    counts.set(c.typeIdx, (counts.get(c.typeIdx) ?? 0) + c.members.length);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([idx, n]) => `${n} ${unitType(idx).name}`)
    .join(', ');
}

/** Total models still standing, for the HUD. */
export function armyStrength(w: World, army: BattleArmy): { alive: number; total: number } {
  let alive = 0;
  let total = 0;
  for (const id of army.companies) {
    const c = findCompany(w, id);
    if (!c) continue;
    alive += c.members.length;
    total += c.maxSize;
  }
  return { alive, total };
}

/** Used by the deployment UI to keep companies inside their zone. */
export function clampToDeployZone(b: BattleState, x: number, y: number): { x: number; y: number } {
  return {
    x: clamp(x, b.deployZone.x0, b.deployZone.x1),
    y: clamp(y, b.deployZone.y0, b.deployZone.y1),
  };
}

export const BATTLE_UNIT_COUNT = UNIT_TYPES.length;
